import { cx } from '../ui/cx.ts';

/**
 * Stacked bar groups with legend chips — Dispatch Deck chart quartet
 * (epic #897). Replaces the hand-rolled `StackedTrendChart` idiom on
 * the Usage page (consumed there in epic PR 6).
 *
 * Series colors cycle a FIXED Deck palette (signal → ok → warn →
 * signal-deep → paper-low → line-strong). There is deliberately no
 * per-series color prop: consumers order series by importance and the
 * cycle does the rest, so charts cannot drift off-token.
 *
 * Accessibility: the svg is exposed as ONE image (`role="img"` +
 * mandatory `ariaLabel` + `<title>`); each bucket `<g>` carries a
 * `<title>` tooltip. Legend chips are visible text; their color
 * swatches are `aria-hidden` decoration. Consumers surface totals as
 * text alongside — the chart is never the sole carrier of a number
 * (DESIGN.md §9).
 *
 * Edges: empty buckets/series render the baseline grid only; an
 * all-zero stack draws no rects (max floored at 1, zero-height bars
 * are skipped).
 */

export interface StackedBarsSeries {
  readonly id: string;
  /** Legend text (already translated / display-ready). */
  readonly label: string;
  /** One value per bucket, aligned by index with `buckets`. */
  readonly values: readonly number[];
}

const FILL_CYCLE = [
  'fill-signal',
  'fill-ok',
  'fill-warn',
  'fill-signal-deep',
  'fill-paper-low',
  'fill-line-strong',
] as const;

const SWATCH_CYCLE = [
  'bg-signal',
  'bg-ok',
  'bg-warn',
  'bg-signal-deep',
  'bg-paper-low',
  'bg-line-strong',
] as const;

/** Exposed so table/legend consumers can echo a series color. */
export function stackedBarsFillClass(index: number): string {
  return FILL_CYCLE[((index % FILL_CYCLE.length) + FILL_CYCLE.length) % FILL_CYCLE.length] ?? 'fill-signal';
}

/**
 * `bg-*` twin of {@link stackedBarsFillClass} for non-SVG consumers
 * (share strips, list swatches) that must echo the same series cycle.
 */
export function stackedBarsSwatchClass(index: number): string {
  return SWATCH_CYCLE[((index % SWATCH_CYCLE.length) + SWATCH_CYCLE.length) % SWATCH_CYCLE.length] ?? 'bg-signal';
}

const VIEW_WIDTH = 960;
const PADDING = 16;
const GAP = 6;

interface Props {
  /** Bucket labels (x axis), display-ready — e.g. `2026-07-01`. */
  readonly buckets: readonly string[];
  readonly series: readonly StackedBarsSeries[];
  /** Rendered pixel height (doubles as the viewBox height). */
  readonly height?: number;
  readonly showLegend?: boolean;
  readonly ariaLabel: string;
  readonly testId?: string;
}

export function StackedBars({
  buckets,
  series,
  height = 288,
  showLegend = true,
  ariaLabel,
  testId,
}: Props): JSX.Element {
  const drawableHeight = height - PADDING * 2;
  const maxStack = Math.max(
    1,
    ...buckets.map((_, bucketIndex) =>
      series.reduce((sum, s) => sum + Math.max(0, s.values[bucketIndex] ?? 0), 0),
    ),
  );
  const barWidth =
    buckets.length === 0
      ? 0
      : Math.max(4, (VIEW_WIDTH - PADDING * 2 - GAP * (buckets.length - 1)) / buckets.length);

  return (
    <div className="flex flex-col gap-3" data-testid={testId}>
      <svg
        role="img"
        aria-label={ariaLabel}
        viewBox={`0 0 ${VIEW_WIDTH} ${height}`}
        preserveAspectRatio="none"
        style={{ height }}
        className="block w-full"
      >
        <title>{ariaLabel}</title>
        {[0, 1, 2].map((line) => {
          const y = PADDING + (line / 2) * drawableHeight;
          return (
            <line
              key={line}
              x1={PADDING}
              x2={VIEW_WIDTH - PADDING}
              y1={y}
              y2={y}
              className="stroke-line-soft"
              strokeWidth={1}
              vectorEffect="non-scaling-stroke"
            />
          );
        })}
        {buckets.map((bucket, bucketIndex) => {
          let yCursor = PADDING + drawableHeight;
          return (
            <g key={`${bucketIndex}-${bucket}`}>
              <title>{bucket}</title>
              {series.map((s, seriesIndex) => {
                const value = Math.max(0, s.values[bucketIndex] ?? 0);
                const barHeight = (value / maxStack) * drawableHeight;
                yCursor -= barHeight;
                return barHeight > 0 ? (
                  <rect
                    key={s.id}
                    x={PADDING + bucketIndex * (barWidth + GAP)}
                    y={yCursor}
                    width={barWidth}
                    height={barHeight}
                    rx={1}
                    className={stackedBarsFillClass(seriesIndex)}
                  />
                ) : null;
              })}
            </g>
          );
        })}
      </svg>
      {showLegend && series.length > 0 ? (
        <div className="flex flex-wrap items-center gap-3">
          {series.map((s, index) => (
            <span
              key={s.id}
              className="inline-flex items-center gap-1.5 text-micro text-paper-low"
            >
              <span
                aria-hidden="true"
                className={cx('h-2 w-2 shrink-0 rounded-[1.5px]', stackedBarsSwatchClass(index))}
              />
              <span className="font-mono">{s.label}</span>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
